import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Project } from "../types/v3types";
import styled from "styled-components";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 32px;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #0056b3;
  }
`;

const BackButton = styled(Button)`
  background-color: #6c757d;

  &:hover {
    background-color: #5a6268;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 15px;
`;

const ProjectName = styled.h2`
  font-size: 20px;
  margin: 0 0 5px 0;
  color: #333;
`;

const ProjectId = styled.div`
  font-size: 14px;
  color: #666;
  font-family: monospace;
`;

const StatusBadge = styled.span<{ isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background-color: ${props => props.isActive ? "#28a745" : "#dc3545"};
  color: white;
`;

const ProjectDescription = styled.p`
  color: #666;
  margin: 15px 0;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProjectMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #888;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.3;
`;

const EmptyStateText = styled.p`
  font-size: 18px;
  margin-bottom: 30px;
`;

const AdminProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<V3Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const projectsQuery = query(
      collection(db, "v3projects"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
      const projectData = snapshot.docs.map(doc => ({
        ...doc.data(),
        projectId: doc.id
      } as V3Project));
      setProjects(projectData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp.toDate) return "不明";
    return timestamp.toDate().toLocaleDateString("ja-JP");
  };

  if (loading) {
    return <Container>読み込み中...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>V3Arena プロジェクト一覧</Title>
        <HeaderButtons>
          <Button onClick={() => navigate("/admin/v3/projects/new")}>
            新規プロジェクト作成
          </Button>
          <BackButton onClick={() => navigate("/admin")}>
            管理画面に戻る
          </BackButton>
        </HeaderButtons>
      </Header>

      {projects.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>📁</EmptyStateIcon>
          <EmptyStateText>
            まだプロジェクトがありません
          </EmptyStateText>
          <Button onClick={() => navigate("/admin/v3/projects/new")}>
            最初のプロジェクトを作成
          </Button>
        </EmptyState>
      ) : (
        <ProjectGrid>
          {projects.map((project) => (
            <ProjectCard
              key={project.projectId}
              onClick={() => navigate(`/admin/v3/projects/${project.projectId}`)}
            >
              <ProjectHeader>
                <div>
                  <ProjectName>{project.projectName}</ProjectName>
                  <ProjectId>{project.projectId}</ProjectId>
                </div>
                <StatusBadge isActive={project.isActive}>
                  {project.isActive ? "アクティブ" : "非アクティブ"}
                </StatusBadge>
              </ProjectHeader>
              
              {project.description && (
                <ProjectDescription>
                  {project.description}
                </ProjectDescription>
              )}
              
              <ProjectMeta>
                <span>作成日: {formatDate(project.createdAt)}</span>
                {project.settings?.maxEventsPerUser && (
                  <span>最大イベント数: {project.settings.maxEventsPerUser}</span>
                )}
              </ProjectMeta>
            </ProjectCard>
          ))}
        </ProjectGrid>
      )}
    </Container>
  );
};

export default AdminProjectList;